import { ref, computed, watch } from 'vue'
import { defineStore } from 'pinia'
import { getVenuesAvailability } from '@/services/ShopAndBook'
import {
  getDateFromUrl,
  getAttendeesFromUrl,
  getPackageIDFromUrl,
  getMeetingRoomFromUrl,
} from '@/utils/project-query-params'
import type { GalaxyPackage } from '@/types/galaxy-data/packages'
import type { SearchPackage } from '@/pages/booking-funnel/types/SearchPackage'
import type { SearchVenue } from '@/pages/booking-funnel/types/SearchVenue'
import { useCartStore } from './cart'
import { useConfigurations } from './configurations'
import type { GalaxyMeetingRoom } from '@/types/galaxy-data/meeting-room'
import type { GalaxyFunctionRoomSetups } from '@/types/galaxy-data/function-room-setups'
import { useWatchQueryParam } from '@/composables/useQueryParams'
import { useFunnelRouter } from '@/AppBookingFunnel/composables/funnel-router'
import IconDbDefaultFunctionRoomSetup from '~icons/db/function-roomsetup-default?raw'
import type { Identifier } from '@/types/galaxy-data/unit'
import type { FilterItem } from '@/types/galaxy-data/filters'
import { useGtm } from '@/composables/features/useGtm'
import { useIdle } from '@vueuse/core'
import { sortMeetingRoomAmenitiesByFlagFeature } from '@/utils/transform-galaxy-data'
const { event } = useGtm()

export const usePackagesStore = defineStore('packages', () => {
  const funnelRouter = useFunnelRouter()
  const configStore = useConfigurations()
  const cartStore = useCartStore()

  /* Top search bar fields */
  const date = ref<Date | undefined>(
    getDateFromUrl(configStore.funnel.minDate, configStore.funnel.maxDate, 'yyyy-MM-dd'),
  )
  const attendees = ref<number | undefined>(getAttendeesFromUrl())
  const packageID = ref<GalaxyPackage | string | null | undefined>(getPackageIDFromUrl(undefined))
  const meetingRoomID = ref<GalaxyMeetingRoom | string | null | undefined>(
    getMeetingRoomFromUrl(undefined),
  )

  useWatchQueryParam('attendees', attendees)
  useWatchQueryParam('date', date)

  const loadingElements = ref(new Set())
  const errorType = ref<string | undefined>('MISSING_BOOKING_PARAM')

  const db2Packages = ref<
    Map<
      string,
      {
        name: string
        description: string
        eventPriceTotal: number
        priceMinPerPerson: number
        id: string
        currencyCode: string
        xTaxes: string
        meetingRooms: Map<
          string,
          {
            minRentalPerPerson: number
            taxes: string
            functionRoomSetups: string[]
            searchTimeSlot: {
              startTime: string
              endTime: string
            }[]
            eventsIds: {
              id: string
              timeSlot: {
                startTime: string
                endTime: string
              }[]
              eventClassificationId: string
            }[]
          }
        >
      }
    >
  >(new Map())
  const allGalaxyPackages = ref<GalaxyPackage[]>([])
  const allGalaxyEventRooms = ref<GalaxyMeetingRoom[]>([])
  const allFunctionRoomSetups = ref<GalaxyFunctionRoomSetups[]>([])

  /**
   * Initial data from galaxy necessary to work
   * For example the galaxy packages, event rooms, etc...
   *
   * Init is done when creating the app
   *
   */
  function init(params: {
    packages: GalaxyPackage[]
    eventRooms: GalaxyMeetingRoom[]
    functionRoomSetups: GalaxyFunctionRoomSetups[]
  }) {
    allGalaxyPackages.value = params.packages
    allGalaxyEventRooms.value = params.eventRooms
    allFunctionRoomSetups.value = params.functionRoomSetups
  }

  watch(
    [date, attendees],
    () => {
      if (!date.value || !attendees.value) {
        errorType.value = 'MISSING_BOOKING_PARAM'
        return
      }

      errorType.value = undefined
      try {
        if (cartStore.cart) {
          cartStore.$reset()
          funnelRouter.goToPackageSelection()
        }
        if (configStore.funnel.locationId) checkPackages(date.value, attendees.value)
      } catch (error) {
        console.error(error)
      }
    },
    { immediate: true },
  )

  // Update the "packageID" query depending on the value
  watch([packageID], () => {
    // When the variable is a object that means the ID has been match the api call
    if (typeof packageID.value === 'object') {
      useWatchQueryParam('packageID', ref<Identifier | undefined>(packageID.value?.db2_packageid))
    } else if (!packageID.value) {
      // Otherwise that means the value has just been get from the url without any check
      useWatchQueryParam('packageID', ref<Identifier | undefined>(packageID.value))
    }
  })

  // Update the "meetingRoomID" query depending on the value
  watch([meetingRoomID], () => {
    // When the variable is a object that means the ID has been match the api call
    if (typeof meetingRoomID.value === 'object') {
      useWatchQueryParam(
        'meetingRoomID',
        ref<Identifier | undefined>(meetingRoomID.value?.db2_eventroomid),
      )
    } else if (!meetingRoomID.value) {
      // Otherwise that means the value has just been get from the url without any check
      useWatchQueryParam('meetingRoomID', ref<Identifier | undefined>(meetingRoomID.value))
    }
  })

  // remove the querries "packageID" and "meetingRoomID" for the deeplink
  function clearDeepLinking() {
    packageID.value = undefined
    meetingRoomID.value = undefined
  }

  function checkPackages(date: Date, attendees: number) {
    if (!configStore.funnel.locationId) {
      throw new Error('Missing location ID')
    }

    loadingElements.value.add('checkPackages')
    db2Packages.value = new Map()

    getVenuesAvailability({
      location: configStore.funnel.locationId,
      date,
      attendees,
      bestFit: !configStore.funnel.showVenueSelection,
      languageCode: window.galaxyInfo?.local,
    })
      .then((packageList) => {
        db2Packages.value = new Map(packageList.map((db2Package) => [db2Package.id, db2Package]))
        // When the packageID has been fetch
        // Check if the "packageID" query URL match with the data
        packageID.value = getPackageIDFromUrl(
          packageList.map((db2Package) => ({ db2_packageid: db2Package.id } as GalaxyPackage)),
        )

        const availableList = allGalaxyPackages.value.filter((galaxypackage) => {
          return db2Packages.value.get(galaxypackage.db2_packageid) ? true : false
        })

        const hotel = window.db2BookingFunnel.getHotel()
        if (availableList.length > 0) {
          event('view_item_list', {
            item_list_id: 'meeting_rooms_packages_list',
            item_list_name: 'Meeting Rooms Packages List',
            items: availableList.map((item, index) => {
              return {
                item_id: item.db2_packageid,
                item_name: item.name,
                affiliation: hotel.name,
                index: index,
                item_brand: hotel.master_item?.name,
                item_category: 'meeting rooms packages',
                item_list_id: 'meeting_rooms_packages_list',
                item_list_name: 'Meeting Rooms Packages List',
                item_variant: '',
                attendees_bucket: '',
                quantity: 1,
              }
            }),
          })
        }
        const { reset } = useIdle()
        reset()
      })
      .catch((error) => {
        console.error(error)
      })
      .finally(() => {
        loadingElements.value.delete('checkPackages')
      })
  }

  const apiLoading = computed<boolean>(() => {
    return loadingElements.value.size > 0
  })

  /**
   * Packages available for the current date & number of attendees
   */
  const availablePackages = computed<SearchPackage[]>(() => {
    if (!db2Packages.value.size) {
      return []
    }

    // We chose to filter then map for readability reasons, as for now
    // we won't have performances issue (small number of packages).
    // Can be re-visited
    return allGalaxyPackages.value
      .filter((galaxypackage) => {
        return db2Packages.value.get(galaxypackage.db2_packageid) ? true : false
      })
      .map((galaxyPackage) => {
        const mapping = db2Packages.value.get(galaxyPackage.db2_packageid)

        const FunctionRoomSetups = getDb2FunctionRoomSetups()
        const functionRoomSetupsList: string[] = []
        const functionRoomSetupsValues: GalaxyFunctionRoomSetups[] = []
        mapping?.meetingRooms.forEach((item) => {
          item.functionRoomSetups.forEach((roomSetup) => {
            if (!functionRoomSetupsList.includes(roomSetup.valueOf())) {
              functionRoomSetupsList.push(roomSetup.valueOf())
              const defaultFunctionRoomSetups = {
                id: -1,
                cle: roomSetup.valueOf(),
                name: roomSetup.valueOf(),
                default_icon: IconDbDefaultFunctionRoomSetup.toString(),
              }
              const val =
                FunctionRoomSetups.find((item) => item.cle === roomSetup.valueOf()) ||
                defaultFunctionRoomSetups
              functionRoomSetupsValues.push(val)
            }
          })
        })
        return {
          ...galaxyPackage,
          functionRoomSetups: functionRoomSetupsValues,
          displayPrice:
            mapping?.currencyCode && mapping?.priceMinPerPerson
              ? {
                  currencyCode: mapping.currencyCode,
                  value: mapping.priceMinPerPerson,
                  valueTotal: () => {
                    return mapping.priceMinPerPerson * (attendees.value || 1)
                  },
                }
              : undefined,
          taxes: mapping ? mapping.xTaxes : 'inclusive',
        }
      })
  })

  function getDb2FunctionRoom(packageId: string, functionRoomId: string) {
    const dataPackage = db2Packages.value.get(packageId)
    if (!dataPackage) {
      throw new Error('package not found')
    }

    const galaxyEventRoom = allGalaxyEventRooms.value.find(
      (eventRoom) => eventRoom.db2_eventroomid === functionRoomId,
    )

    const apiData = dataPackage.meetingRooms.get(functionRoomId)
    if (!apiData) {
      return undefined
      //throw new Error('meeting room not found')
    }

    return {
      apiData,
      galaxyEventRoom,
    }
  }

  function getDb2Package(packageId: string) {
    const dataPackage = db2Packages.value.get(packageId)
    if (!dataPackage) {
      throw new Error('package not found')
    }

    const galaxyPackage = allGalaxyPackages.value.find(
      (eventRoom) => eventRoom.db2_packageid === packageId,
    )

    return {
      apiData: dataPackage,
      galaxyPackage,
    }
  }

  function getDb2FunctionRoomSetups() {
    return allFunctionRoomSetups.value
  }

  const availableVenues = computed<SearchVenue[]>(() => {
    const currentPackageId = cartStore.cart?.packageId
    if (!currentPackageId) {
      return []
    }
    const dataPackage = db2Packages.value.get(currentPackageId)
    if (!dataPackage) {
      return []
    }
    // When the meetingRoom has been fetch
    // Check if the "meetingRoom" query URL match with the data
    meetingRoomID.value = getMeetingRoomFromUrl(allGalaxyEventRooms.value)

    return sortMeetingRoomAmenitiesByFlagFeature(allGalaxyEventRooms.value)
      .filter((galaxyEventRoom) => {
        return dataPackage.meetingRooms.get(galaxyEventRoom.db2_eventroomid) ? true : false
      })
      .map((galaxyEventRoom) => {
        const mapping = dataPackage.meetingRooms.get(galaxyEventRoom.db2_eventroomid)
        return {
          ...galaxyEventRoom,
          functionRoomSetups: mapping?.functionRoomSetups, // TODO move to a better place
          functionRoomEvents: mapping?.eventsIds, // TODO move to a better place
          displayPrice: mapping?.minRentalPerPerson
            ? {
                currencyCode: dataPackage?.currencyCode || 'USD',
                value: mapping?.minRentalPerPerson + (dataPackage.eventPriceTotal || 0),
                valueTotal: () => {
                  return (
                    ((dataPackage.eventPriceTotal || 0) + mapping?.minRentalPerPerson) *
                    (attendees.value || 1)
                  )
                },
              }
            : undefined,
          taxes: mapping ? mapping.taxes : 'inclusive',
        }
      })
  })

  const searchFilters: FilterItem[] = []

  function displayTotalPriceToggle() {
    return configStore.general.rateDisplaySwitch
  }

  function defaultSeeTotalPrice() {
    return configStore.general.rateDisplay
  }

  function displayStartEndTimeTimeSlot() {
    return configStore.general.startEndTimeDisplay
  }

  return {
    init,
    date,
    attendees,
    packageID,
    meetingRoomID,
    clearDeepLinking,
    availablePackages,
    availableVenues,
    errorType,
    apiLoading,
    searchFilters,
    getDb2FunctionRoom,
    getDb2Package,
    getDb2FunctionRoomSetups,
    displayTotalPriceToggle,
    defaultSeeTotalPrice,
    displayStartEndTimeTimeSlot,
  }
})
